/**
 * The plant check-up ("What's wrong with it?"): a whole plant, matched
 * against the problems this kind of plant actually gets.
 *
 * The server sends the plant's history, notes, weather and up to ten of its
 * photographs, spread across its life. Likely problems come back in text
 * written here for gardeners, each headed by its common name ("Blossom end
 * rot") with its cause under it, a fuller answer in six parts (What it is,
 * Why it happens, What to do now, Next season, Does it spread?, Is the fruit
 * still good to eat?; the most likely one adapted to this plant) and "Why we
 * think so" in three short parts (what you said, your photos, the weather).
 * How sure the check-up is only orders them: the app says "Most likely" and
 * "Also possible". Only when nothing on the list fits does the answer carry
 * Grow's own suggestion, which the app labels as such. Everything the model
 * writes is to the gardener ("you"), in their language.
 *
 * The first answer comes with up to three follow-up questions to tap
 * (answerFollowUp): at most two rounds, each one more AI request, then the
 * answer is final. Every answer carries the journal note it can be saved as.
 * About a week later the check-up asks once whether it turned out to be the
 * likely problem (getDoctorStatus, answerOutcome).
 *
 * See the backend's routes/plantDoctor.js.
 *
 * Before the look the gardener says what they see ("What are you seeing?":
 * bugs, holes, spots, yellowing, something else; with bugs, their colour and
 * how they act). The server owns those choices and their words
 * (getSeeingChoices); the app shows them and sends back the ids, which go to
 * the model as facts.
 *
 * Endpoints (every answer also carries `success`):
 * - GET  /plant-doctor/seeing-choices?lang= → SeeingChoices.
 * - POST /plant-doctor/:plantObjId?tz= { concern, lang, seeing } → PlantDiagnosis
 *   (round 0). Slow: PLANT_DOCTOR_REQUEST_TIMEOUT_MS, not the usual 30 s.
 * - POST /plant-doctor/checkups/:checkupId/answers?tz= { answers, lang } →
 *   PlantDiagnosis (rounds 1 and 2; the server keeps the rounds on the check-up).
 * - POST /plant-doctor/checkups/:checkupId/skip {} → PlantDiagnosis (the first
 *   look's held-back answer, skipped). A first look with questions arrives
 *   withheld: the questions, and no likely problem until they are answered or skipped.
 * - GET  /plant-doctor/status → DoctorStatus.
 * - POST /plant-doctor/checkups/:checkupId/outcome { answer } and
 *   /outcome-asked {} → nothing to read.
 *
 * Refusals: 402 SUBSCRIPTION_REQUIRED_AI (Free, a paused plan: the API
 * client raises the subscription wall), 403 AI_REVOKED (switched off by
 * the owner), 429 AI_LIMIT with an AI_LIMIT_MONTH_DOCTOR, _DAY or _TRIAL
 * code (over the photo help's allowance, features/billing/lib/aiLimit), and
 * PLANT_HAS_NO_PHOTOS. diagnosisErrorCode reads the code.
 *
 * Who uses it: features/plant-list (PlantDoctor, DoctorMoments,
 * useDoctorStatus, DoctorSeeing). Caching: the seeing choices are cached for
 * the session by DoctorSeeing (SEEING_CHOICES_QUERY_KEY, by language); the status under
 * PLANT_DOCTOR_STATUS_QUERY_KEY (['plant-doctor', 'status'],
 * PLANT_DOCTOR_STATUS_STALE_MS), invalidated after a check-up or an
 * outcome; the check-up itself is a mutation. Auth: the signed-in token,
 * in the garden being tended (X-Grow-Garden), spending that garden owner's
 * allowance.
 *
 * @module diagnose
 */

import axios from 'axios';
import { apiClient } from './apiClient';
import { aiDateParams } from './aiAllowance';
import { PLANT_DOCTOR_REQUEST_TIMEOUT_MS } from '@/config/tunables/plantList';
import { trackEvent } from '@/shared/lib/analytics';

/** A problem this kind of plant gets, as written for gardeners. */
export interface PlantProblem {
  /** What a gardener would see, in plain words. */
  lookFor: string;
  /** The cause. */
  why: string;
  /** What to do about it. */
  fix: string;
}

/**
 * "Why we think so" in three short parts; a part is null when the model had
 * nothing for it. The weather part is shown only when the weather points to
 * the problem: the numbers on record (facts), then what they meant.
 */
export interface CheckupReasons {
  /** What the gardener told the check-up that points to it. */
  youSaid: string | null;
  /** What their photos show, each named by its date. */
  yourPhotos: string | null;
  /** "Sep 1–14 at Raleigh-Durham International Airport, 12 mi away: highs up to 101°F, …", then its meaning (null when the model only repeated the numbers). */
  weather: { facts: string | null; meaning: string | null } | null;
}

/**
 * The fuller answer to a problem, in six parts; a part is null when there is
 * nothing true to say about it for this plant, and the card leaves it out.
 */
export interface CheckupAnswer {
  /** "What it is". */
  whatItIs: string | null;
  /** "Why it happens". */
  why: string | null;
  /** "What to do now". */
  doNow: string | null;
  /** "Next season". */
  nextSeason: string | null;
  /** "Does it spread?". */
  spreads: string | null;
  /** "Is the fruit still good to eat?". */
  stillEdible: string | null;
}

/** One problem from our list that the plant likely has. */
export interface DiagnosisMatch {
  /** The problem from the crop's list, in gardeners' words. */
  problem: PlantProblem;
  /** The one problem it is, by its cause. */
  name: string;
  /** The name gardeners know it by ("Blossom end rot"), the heading; null when the heading is name. */
  commonName: string | null;
  /** The fuller answer in six parts (services/grow/doctorReply answerFor). */
  answer: CheckupAnswer;
  /** How sure the match is, 0-100, only for ordering (never shown); null when the model did not say. */
  confidence: number | null;
  /** Why the check-up thinks so, as one paragraph (what the note keeps); null when it gave none. */
  evidence: string | null;
  /** The same in three labelled parts; null when the check-up gave none. */
  reasons: CheckupReasons | null;
  /** Serious or spreading (a blight, a virus), as the model flagged it. */
  serious: boolean;
}

/** A follow-up question, answered by tapping one of its options. */
export interface FollowUpQuestion {
  /** "q1", "q2"… within its round. */
  id: string;
  /** The question, in the gardener's language. */
  text: string;
  /** The answers to tap ("Old", "New", "Both"). */
  options: string[];
  /** The problems a No rules out, as the server keys them; only sent back, never shown. */
  tests?: string[];
}

/** One tappable choice under "What are you seeing?". */
export interface SeeingChoice {
  /** Sent back to the server. */
  id: string;
  /** What the gardener reads, in their language. */
  label: string;
}

/** One question of "What are you seeing?": its heading and its choices. */
export interface SeeingQuestion {
  /** "What are you seeing?", "What colour are the bugs?"… */
  heading: string;
  /** Its choices, in order. */
  choices: SeeingChoice[];
}

/** The "What are you seeing?" questions, in the gardener's language; colour and habit only with bugs. */
export interface SeeingChoices {
  /** What they see on the plant; one or more. */
  what: SeeingQuestion;
  /** The bugs' colour; one. */
  colour: SeeingQuestion;
  /** How the bugs act; one. */
  habit: SeeingQuestion;
}

/** What the gardener tapped under "What are you seeing?". */
export interface ReportedSeeing {
  /** The ids of what they see; at least one. */
  what: string[];
  /** The bugs' colour id, with bugs. */
  bugColour?: string | null;
  /** How the bugs act, with bugs. */
  bugHabit?: string | null;
}

/** The choice id that asks the bug questions. */
export const SEEING_BUGS_ID = 'bugs';

/** What looking at the whole plant found. */
export interface PlantDiagnosis {
  /** True when the plant matched at least one problem we know. */
  found: boolean;
  /** A few sentences on how the plant has been doing over time. */
  summary: string | null;
  /** One sentence on what is visible in the newest photograph. */
  seeing: string | null;
  /** Likely problems from our list, most likely first; empty when none matched. */
  matches: DiagnosisMatch[];
  /** Grow's own suggestion, only when nothing on our list fitted a problem it saw. */
  own: (PlantProblem & {
    confidence: number | null;
    evidence: string | null;
    reasons: CheckupReasons | null;
    serious: boolean;
    /** Its common name, when the check-up gave one. */
    commonName: string | null;
    /** What to look for and what to do, as the six parts' first and third. */
    answer: CheckupAnswer;
  }) | null;
  /** Photographs the model was shown. */
  photosLooked: number;
  /** Photographs the plant has. */
  photosTotal: number;
  /** The kept check-up, which follow-up answers go to; null when it could not be kept. */
  checkupId: number | null;
  /** 0 for the first answer, then 1 and 2 for the follow-up rounds. */
  round: number;
  /** The next round's questions; empty when there is none (the answer is final). */
  questions: FollowUpQuestion[];
  /**
   * Why to point to the county extension office, or null: a problem that needs
   * a lab to tell apart ('lab'), Grow unsure between two or of its one guess
   * ('unsure'), or nothing recognised ('unknown').
   */
  extension: { kind: 'lab' | 'unsure' | 'unknown'; problem: string | null; other: string | null } | null;
  /** The check-up as a journal note, ready to save. */
  note: string;
  /** A photo seems to show a different plant: the app asks the gardener to check, in one quiet line. */
  otherPlantPhoto: boolean;
  /**
   * The line the answer ends with, the server's decision (bloom-backend services/grow/doctorClosing, B75):
   * 'matches' (the likely problems), 'own' (Grow's own suggestion), 'wrongPhoto' (nothing fits and a photo looked
   * like a different plant: ask for a photo of this plant, never "it is fine"), 'fine' (nothing fits, and the
   * photos are of this plant). Null on a withheld answer (C118 F3: always sent, none worked out in the app).
   */
  closing: 'matches' | 'own' | 'wrongPhoto' | 'fine' | null;
  /**
   * The answer is held back while its questions wait, at any round (questions first, the analysis at
   * the end): only the questions and what was looked at are sent; skipFollowUp gives the held answer.
   * Optional because the server (doctorCheckups withheldView) sends it only on such an answer.
   */
  withheld?: boolean;
  /**
   * The gardener skipped the questions: this is the first look's answer, final. Optional because
   * the server (doctorCheckups skipRound) sends it only on the answer a skip gives back.
   */
  skipped?: boolean;
}

/** Whether a check-up can be had now, and the week-later questions due. */
export interface DoctorStatus {
  /** Whether this plan offers check-ups. */
  checkup: {
    /** The trial and Grow More: yes; Free and a paused plan: no. */
    allowed: boolean;
  };
  /** "Did it turn out to be …?", one per plant, newest first. */
  outcomes: OutcomeQuestion[];
}

/** A week-later question due for a check-up. */
export interface OutcomeQuestion {
  /** The check-up it asks about. */
  checkupId: number;
  /** The plant it was for. */
  plantObjId: number;
  /** The plant's name, for the question. */
  plantName: string;
  /** The likely problem it named. */
  problem: string;
  /** The check-up's day, "YYYY-MM-DD". */
  checkedOn: string;
}

/** The week-later answers. */
export type OutcomeAnswer = 'yes' | 'else' | 'unsure';

/** The server's code when the plant has no photograph to look at. */
export const PLANT_HAS_NO_PHOTOS = 'PLANT_HAS_NO_PHOTOS';
/** The server's code when this plan has no check-ups (Free, a paused plan). */
export const SUBSCRIPTION_REQUIRED_AI = 'SUBSCRIPTION_REQUIRED_AI';
/** The server's code (403) when the owner has switched AI off for this account, whatever the plan. */
export const AI_REVOKED = 'AI_REVOKED';

/**
 * Asks what is wrong with a plant, looking at all of it. The trial's and Grow
 * More's (never Free's): a refusal arrives as a 402 SUBSCRIPTION_REQUIRED_AI.
 *
 * @param plantObjId - The plant.
 * @param concern - What the gardener found, in their own words. Required.
 * @param lang - The gardener's language: the check-up writes in it, and the journal note is in it.
 * @param seeing - What they tapped under "What are you seeing?"; the model takes it as facts.
 * @returns The first answer.
 * @throws The request's error; {@link diagnosisErrorCode} reads its code.
 */
export async function diagnosePlant(
  plantObjId: number,
  concern: string,
  lang?: string,
  seeing?: ReportedSeeing | null,
): Promise<PlantDiagnosis> {
  const response = await apiClient.post<PlantDiagnosis & { success: boolean }>(
    `/plant-doctor/${plantObjId}`,
    { concern: concern.trim(), lang, seeing: seeing ?? undefined },
    { timeout: PLANT_DOCTOR_REQUEST_TIMEOUT_MS, params: aiDateParams() }
  );
  trackEvent('checkup');
  return response.data;
}

/**
 * Answers a round of follow-up questions, by tapping, and gets the updated answer.
 *
 * @param checkupId - The check-up.
 * @param answers - One tapped option per question answered.
 * @param lang - The gardener's language, for the journal note.
 * @returns The updated answer; its questions are the next round's (none after the last).
 * @throws The request's error; {@link diagnosisErrorCode} reads its code.
 */
export async function answerFollowUp(
  checkupId: number,
  answers: Array<{ id: string; answer: string }>,
  lang?: string,
): Promise<PlantDiagnosis> {
  const response = await apiClient.post<PlantDiagnosis & { success: boolean }>(
    `/plant-doctor/checkups/${checkupId}/answers`,
    { answers, lang },
    { timeout: PLANT_DOCTOR_REQUEST_TIMEOUT_MS, params: aiDateParams() }
  );
  return response.data;
}

/**
 * Skips the follow-up questions, at any round: the newest answer, held back
 * while they waited (the first look's or the last round's), final. No AI
 * request; the check-up ends.
 *
 * @param checkupId - The check-up.
 * @returns That answer, with no questions (skipped).
 * @throws The request's error (409 NOTHING_TO_SKIP when nothing is held back); {@link diagnosisErrorCode} reads its code.
 */
export async function skipFollowUp(checkupId: number): Promise<PlantDiagnosis> {
  const response = await apiClient.post<PlantDiagnosis & { success: boolean }>(`/plant-doctor/checkups/${checkupId}/skip`, {});
  return response.data;
}

/**
 * The "What are you seeing?" questions and their words, in the gardener's language.
 *
 * @param lang - The gardener's language ('es' for Spanish; English otherwise).
 * @returns The three questions.
 * @throws The request's error.
 */
export async function getSeeingChoices(lang?: string): Promise<SeeingChoices> {
  const response = await apiClient.get<SeeingChoices & { success: boolean }>('/plant-doctor/seeing-choices', { params: { lang } });
  return { what: response.data.what, colour: response.data.colour, habit: response.data.habit };
}

/**
 * What the Doctor's window says about a plant's newest photo, by the
 * check-up's own rules (the server's; audit batch 4, A28).
 */
export interface DoctorPhotoStatus {
  /** The newest photo's day, "YYYY-MM-DD"; null with none dated. */
  newestOn: string | null;
  /** Whole days from it to the gardener's today; null with none dated. */
  ageDays: number | null;
  /** The check-up will send this week's forecast (a postcode, not sheltered, a recent photo). */
  forecastUsed: boolean;
  /** Too old for the forecast: the answer asks for a recent photo. */
  photoOld: boolean;
  /** Older than the Grow setting photoai.DOCTOR_STALE_PHOTO_DAYS: the window warns before the check. */
  photoStale: boolean;
}

/**
 * A plant's photo status for the Doctor's window (GET /plant-doctor/status?plantObjId=).
 *
 * @param plantObjId - The plant.
 * @returns The status; null for a plant not in this garden or with no photo.
 */
export async function getDoctorPhotoStatus(plantObjId: number): Promise<DoctorPhotoStatus | null> {
  const response = await apiClient.get<{ success: boolean; plant: DoctorPhotoStatus | null }>('/plant-doctor/status', { params: { plantObjId } });
  return response.data.plant;
}

/**
 * Whether a check-up can be had now, and the week-later questions due.
 *
 * @returns The status.
 */
export async function getDoctorStatus(): Promise<DoctorStatus> {
  const response = await apiClient.get<DoctorStatus & { success: boolean }>('/plant-doctor/status');
  return { checkup: response.data.checkup, outcomes: response.data.outcomes };
}

/**
 * Keeps the week-later answer.
 *
 * @param checkupId - The check-up.
 * @param answer - Yes, something else, or not sure.
 */
export async function answerOutcome(checkupId: number, answer: OutcomeAnswer): Promise<void> {
  await apiClient.post(`/plant-doctor/checkups/${checkupId}/outcome`, { answer });
}

/**
 * Notes that the week-later question was shown, so it is never asked again.
 *
 * @param checkupId - The check-up.
 */
export async function markOutcomeAsked(checkupId: number): Promise<void> {
  await apiClient.post(`/plant-doctor/checkups/${checkupId}/outcome-asked`, {});
}

/**
 * The code and message the server refused a look with, when it gave them.
 *
 * @param error - What {@link diagnosePlant} threw.
 * @returns The code (e.g. {@link PLANT_HAS_NO_PHOTOS}) and message; nulls otherwise.
 */
export function diagnosisErrorCode(error: unknown): { code: string | null; message: string | null } {
  if (!axios.isAxiosError(error)) return { code: null, message: null };
  const data = error.response?.data as { code?: string; message?: string } | undefined;
  return { code: data?.code ?? null, message: data?.message ?? null };
}
